import {
  getApiErrorI18nKey,
  useAllSchemas,
  useCorpusAccess,
  useCorpusDocuments,
  useCorpusOverview,
} from "@noa/api-client";
import type { CorpusDocumentRow } from "@noa/api-core";
import { RotateCw, Trash2, Upload } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { type CorpusDeletion, CorpusDeletionDialog } from "~/components/admin/vectorstore/corpus-deletion-dialog";
import { CorpusDocumentDialog } from "~/components/admin/vectorstore/corpus-document-dialog";
import { CorpusDocumentTable } from "~/components/admin/vectorstore/corpus-document-table";
import { CorpusErrorAlert } from "~/components/admin/vectorstore/corpus-error-alert";
import { CorpusFilterBar } from "~/components/admin/vectorstore/corpus-filter-bar";
import { CorpusOverview } from "~/components/admin/vectorstore/corpus-overview";
import { Alert, Button, Empty, Text, Title } from "~/components/ui";
import { useDebounce } from "~/hooks/use-debounce";
import { Screen } from "~/screens/screen";
import {
  type CorpusFilterForm,
  describeFilters,
  EMPTY_FILTER_FORM,
  filterableTextFields,
  hasAnyFilter,
  toCorpusFilters,
} from "~/utils/corpus-admin";

const PAGE_SIZE = 50;

const UPLOADS_ROUTE = "/admin/uploads";

/** The vectorstore admin: what is ingested, each document's chunks, and deleting documents. */
export function VectorstoreAdminScreen() {
  const { t, i18n } = useTranslation("admin");
  const navigate = useNavigate();

  const [form, setForm] = useState<CorpusFilterForm>(EMPTY_FILTER_FORM);
  const [page, setPage] = useState(1);
  const [opened, setOpened] = useState<CorpusDocumentRow | null>(null);
  const [deletion, setDeletion] = useState<CorpusDeletion | null>(null);

  // The list follows the name once typing pauses
  const debouncedName = useDebounce(form.name, 300);
  const sentForm = { ...form, name: debouncedName };
  const filters = toCorpusFilters(sentForm);

  const access = useCorpusAccess();
  const overview = useCorpusOverview();
  const documents = useCorpusDocuments({ filters, page, page_size: PAGE_SIZE });
  const { schemas } = useAllSchemas();

  const filtered = hasAnyFilter(filters);
  const listed = !documents.isLoading && !documents.isError;
  // The list answers this very query, not the previous one standing in until it does
  const settled = listed && !documents.isPlaceholderData;
  const lastPage = Math.max(1, Math.ceil(documents.total / PAGE_SIZE));
  // A page emptied under the admin, its last rows deleted, gives way to the new last one
  if (settled && page > lastPage) {
    setPage(lastPage);
  }
  const countText = t("vectorstoreAdmin.count", {
    count: documents.total,
    formattedCount: documents.total.toLocaleString(i18n.language),
  });

  const handleFormChange = (next: CorpusFilterForm) => {
    setForm(next);
    // A new selection starts on its first page
    setPage(1);
  };

  const handleRefresh = () => {
    void overview.refetch();
    void documents.refetch();
  };

  const handleDeleteMatching = () =>
    setDeletion({ request: { filters }, selection: { kind: "filters", filters: describeFilters(sentForm) } });

  const handleDeleteOne = (document: CorpusDocumentRow) =>
    setDeletion({
      request: { source_urls: [document.source_url] },
      selection: { kind: "document", name: document.name, sourceUrl: document.source_url },
    });

  const emptyState = () => {
    // Nothing to say before this query's answer: the spinner covers this space
    if (!settled) return <div className="h-32" />;
    if (filtered) {
      return (
        <Empty description={t("vectorstoreAdmin.emptyFiltered")}>
          <Button onClick={() => handleFormChange(EMPTY_FILTER_FORM)}>{t("vectorstoreAdmin.filters.reset")}</Button>
        </Empty>
      );
    }
    return (
      <Empty description={t("vectorstoreAdmin.emptyCorpus")}>
        <Button icon={<Upload size={14} />} onClick={() => navigate(UPLOADS_ROUTE)}>
          {t("vectorstoreAdmin.goToUploads")}
        </Button>
      </Empty>
    );
  };

  return (
    <Screen>
      <div className="flex h-full w-full flex-col overflow-auto p-4 sm:p-6">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <Title level={3} className="m-0">
              {t("vectorstoreAdmin.title")}
            </Title>
            <Text tone="muted">{t("vectorstoreAdmin.subtitle")}</Text>
          </div>
          <Button icon={<RotateCw size={14} />} onClick={handleRefresh}>
            {t("vectorstoreAdmin.refresh")}
          </Button>
        </div>

        {overview.isError ? (
          <Alert
            type="error"
            showIcon
            className="mb-4"
            message={t("vectorstoreAdmin.error.title")}
            description={t(getApiErrorI18nKey(overview.error))}
          />
        ) : (
          <CorpusOverview overview={overview.overview} isLoading={overview.isLoading} detailed={access.canDelete} />
        )}

        <CorpusFilterBar
          form={form}
          onChange={handleFormChange}
          overview={overview.overview}
          fields={filterableTextFields(schemas, form.documentType)}
        />

        <div className="flex-1 rounded-lg p-4 bg-noa">
          {/* Always mounted, so a screen reader hears what each new filter selects */}
          <p className="sr-only" aria-live="polite">
            {listed ? countText : ""}
          </p>
          {listed && (
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <Text strong>{countText}</Text>
              {access.canDelete && filtered && documents.total > 0 && (
                <Button
                  danger
                  icon={<Trash2 size={14} />}
                  // The count must be the one the filters now select, the name being typed included
                  disabled={documents.isFetching || form.name !== debouncedName}
                  onClick={handleDeleteMatching}
                >
                  {t("vectorstoreAdmin.actions.deleteMatching", {
                    count: documents.total,
                    formattedCount: documents.total.toLocaleString(i18n.language),
                  })}
                </Button>
              )}
            </div>
          )}

          {documents.isError ? (
            <CorpusErrorAlert error={documents.error} />
          ) : (
            <CorpusDocumentTable
              documents={documents.documents}
              total={documents.total}
              page={page}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              loading={documents.isFetching}
              onView={setOpened}
              onDelete={access.canDelete ? handleDeleteOne : undefined}
              showChunks={access.canDelete}
              emptyContent={emptyState()}
            />
          )}
        </div>
      </div>

      <CorpusDocumentDialog
        canDelete={access.canDelete}
        sourceUrl={opened?.source_url ?? null}
        name={opened?.name ?? ""}
        onClose={() => setOpened(null)}
      />
      <CorpusDeletionDialog deletion={deletion} onClose={() => setDeletion(null)} />
    </Screen>
  );
}
